// Client for the on-demand radar render service (services/radar-render). Prefers the
// /render endpoint (VITE_RADAR_RENDER_API); if that isn't configured, falls back to any
// on-demand artifacts already published under R2 (radar/ondemand/…/latest.json). PURE
// data layer (fetch only) — the resulting RadarRender feeds the layer framework + the
// value-grid inspector. No DOM, no Mapbox.

import { config } from '@/config'
import type { ValueGridMeta } from './valuegrid'

export interface RadarRender {
  site: string
  name: string
  product: string
  tilt: number
  /** ISO8601 Z scan time. */
  scan: string
  /** Ascending available REF tilts in the volume. */
  tilts: number[]
  /** 4 WGS84 corners [TL, TR, BR, BL] as [lng, lat] for a Mapbox image source. */
  corners: [[number, number], [number, number], [number, number], [number, number]]
  /** [w, s, e, n] in EPSG:3857 meters. */
  bounds3857: [number, number, number, number]
  siteLat: number
  siteLon: number
  imageUrl: string
  valuesUrl: string
  grid: ValueGridMeta
}

interface GridJson {
  nx: number
  ny: number
  k: number
  encoding: string
  nodata: number
  dbz_min: number
  dbz_max: number
}

/** bounds.json / latest.json on R2 (image/values are filenames relative to the scan dir). */
interface BoundsJson {
  site: string
  name: string
  product: string
  tilt: number
  scan: string
  tilts: number[]
  corners: [[number, number], [number, number], [number, number], [number, number]]
  bounds3857: [number, number, number, number]
  site_lat: number
  site_lon: number
  grid: GridJson
  image: string
  values: string
}

/** The /render endpoint response (URLs are absolute or root-relative). */
interface RenderResponse {
  ok: boolean
  site: string
  name: string
  product: string
  tilt: number
  scan: string
  tilts: number[]
  corners: [[number, number], [number, number], [number, number], [number, number]]
  bounds3857: [number, number, number, number]
  site_lat: number
  site_lon: number
  image: string
  meta: string
  values: string
  grid?: GridJson
  error?: string
  coming_soon?: boolean
}

/** Resolve a possibly-relative service URL against the render API / R2 origin. Pure: it
 *  resolves only from config (no DOM), and passes a relative path through unchanged when
 *  no base is configured. */
function resolveUrl(u: string): string {
  if (/^https?:\/\//.test(u)) return u
  const base = config.map.renderApi || config.r2.base
  if (!base) return u
  try {
    return new URL(u, base).toString()
  } catch {
    return u
  }
}

/** scan ISO → compact dir token (must match the service's scan_compact: strip non-alnum). */
const scanDir = (scan: string): string => scan.replace(/[^0-9A-Za-z]/g, '')

const tiltDir = (tilt: number): string => `t${String(Math.round(tilt * 10)).padStart(3, '0')}`

const gridMeta = (g: GridJson | undefined): ValueGridMeta =>
  g ? { nx: g.nx, ny: g.ny, nodata: g.nodata } : { nx: 0, ny: 0, nodata: -128 }

export class ProductUnavailableError extends Error {}

/** Request (or fetch the cached) on-demand render for a site / product / tilt. */
export async function requestRender(site: string, product = 'ref', tilt = 0.5): Promise<RadarRender> {
  if (config.map.renderApi) return renderViaApi(site, product, tilt)
  return renderViaR2(site, product, tilt)
}

async function renderViaApi(site: string, product: string, tilt: number): Promise<RadarRender> {
  const url = `${config.map.renderApi}/render?site=${encodeURIComponent(site)}&product=${encodeURIComponent(
    product,
  )}&tilt=${tilt}`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as RenderResponse
    if (body.coming_soon) throw new ProductUnavailableError(body.error || 'product not available')
  }
  if (!res.ok) throw new Error(`render ${res.status}`)
  const j = (await res.json()) as RenderResponse
  if (!j.ok) throw new Error(j.error || 'render failed')

  // Grid dims may be inlined on the response; otherwise read them from the meta JSON.
  let grid = j.grid
  if (!grid && j.meta) {
    const mres = await fetch(resolveUrl(j.meta), { headers: { Accept: 'application/json' } }).catch(() => null)
    if (mres?.ok) grid = ((await mres.json()) as BoundsJson).grid
  }

  return {
    site: j.site,
    name: j.name,
    product: j.product,
    tilt: j.tilt,
    scan: j.scan,
    tilts: j.tilts,
    corners: j.corners,
    bounds3857: j.bounds3857,
    siteLat: j.site_lat,
    siteLon: j.site_lon,
    imageUrl: resolveUrl(j.image),
    valuesUrl: resolveUrl(j.values),
    grid: gridMeta(j.grid ?? grid),
  }
}

/** Fallback: read the on-demand `latest.json` pointer published under R2 (no live API). */
async function renderViaR2(site: string, product: string, tilt: number): Promise<RadarRender> {
  if (!config.r2.base) throw new Error('no render API and no R2 base configured')
  const dir = `${config.r2.base}/radar/ondemand/${encodeURIComponent(site.toUpperCase())}/${encodeURIComponent(
    product,
  )}/${tiltDir(tilt)}`
  const res = await fetch(`${dir}/latest.json`, { headers: { Accept: 'application/json' }, cache: 'no-cache' })
  if (!res.ok) throw new Error(`radar latest ${res.status}`)
  const j = (await res.json()) as BoundsJson
  const sd = `${dir}/${scanDir(j.scan)}`
  return {
    site: j.site,
    name: j.name,
    product: j.product,
    tilt: j.tilt,
    scan: j.scan,
    tilts: j.tilts,
    corners: j.corners,
    bounds3857: j.bounds3857,
    siteLat: j.site_lat,
    siteLon: j.site_lon,
    imageUrl: `${sd}/${j.image}`,
    valuesUrl: `${sd}/${j.values}`,
    grid: gridMeta(j.grid),
  }
}
